import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  inventoryCountService,
  InventoryCountItemsListResponse,
  InventoryCountItemSummary,
} from '../../../../../core/modules/inventoryCount/services/api';

interface Props {
  companyId: number;
  sessionId: number;
  method: 'scanner' | 'camera';
}

const speak = (text: string) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'es-CL';
  window.speechSynthesis.speak(utterance);
};

// Los lectores fisicos suelen venir configurados para teclado en ingles (US);
// si el computador tiene el teclado en español, simbolos como "-" pueden salir
// como otro caracter (ej. "'"). Para no depender de eso, comparamos solo
// letras y numeros, ignorando cualquier separador.
const normalize = (code: string) => code.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

// Evita contar dos veces el mismo codigo si la camara lo vuelve a detectar
// en el frame siguiente.
const CAMERA_SAME_CODE_COOLDOWN_MS = 1500;

const ScanCountingPanel = ({ companyId, sessionId, method }: Props) => {
  const navigate = useNavigate();

  const [data, setData] = useState<InventoryCountItemsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tallies, setTallies] = useState<Record<number, number>>({});
  const [submittingProductId, setSubmittingProductId] = useState<number | null>(null);
  const [lastScan, setLastScan] = useState<{ sku: string; productName: string; location: string | null; tally: number } | null>(null);
  const [scannerInput, setScannerInput] = useState('');

  const scannerInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastCameraScanRef = useRef<{ code: string; at: number } | null>(null);
  const tallyRef = useRef<Record<number, number>>({});

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryCountService.listSessionItems(companyId, sessionId, 'all');
      setData(result);
      const initialTallies: Record<number, number> = {};
      result.items.forEach((item) => {
        initialTallies[item.productId] = item.countedQty ? Math.floor(Number(item.countedQty)) : 0;
      });
      setTallies(initialTallies);
      tallyRef.current = initialTallies;
    } catch (err) {
      setError('No se pudo cargar el listado de productos a contar.');
    } finally {
      setLoading(false);
    }
  }, [companyId, sessionId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Indice para reconocer un codigo escaneado, ya sea que coincida con el
  // SKU o con el codigo de barras del producto.
  const lookup = useMemo(() => {
    const map = new Map<string, InventoryCountItemSummary>();
    (data?.items || []).forEach((item) => {
      if (item.sku) map.set(normalize(item.sku), item);
      if (item.barcode) map.set(normalize(item.barcode), item);
    });
    return map;
  }, [data]);

  const countedProducts = useMemo(
    () => Object.values(tallies).filter((qty) => qty > 0).length,
    [tallies]
  );
  const total = data?.total ?? 0;
  const percent = total ? Math.round((countedProducts / total) * 1000) / 10 : 0;

  const handleScan = useCallback(
    async (rawCode: string) => {
      const code = normalize(rawCode);
      if (!code) return;

      const item = lookup.get(code);
      if (!item) {
        setError(`Código "${rawCode}" no reconocido en esta sesión.`);
        return;
      }

      setError(null);
      const newTally = (tallyRef.current[item.productId] ?? 0) + 1;
      setSubmittingProductId(item.productId);
      try {
        await inventoryCountService.submitCount(companyId, sessionId, item.productId, {
          countedQty: newTally,
          method,
        });
        tallyRef.current = { ...tallyRef.current, [item.productId]: newTally };
        setTallies(tallyRef.current);
        setLastScan({ sku: item.sku, productName: item.productName, location: item.location, tally: newTally });
        speak(item.productName);
      } catch (err) {
        setError('No se pudo registrar el conteo de ese SKU. Intenta escanearlo de nuevo.');
      } finally {
        setSubmittingProductId(null);
      }
    },
    [companyId, sessionId, method, lookup]
  );

  // --- Modo escaner fisico (USB/Bluetooth): actua como teclado, termina con Enter ---
  const handleScannerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = scannerInput;
      setScannerInput('');
      handleScan(value);
    }
  };

  // --- Modo camara: decodifica continuamente el video con @zxing/browser ---
  useEffect(() => {
    if (method !== 'camera' || !videoRef.current) return undefined;

    let controls: IScannerControls | undefined;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();

    reader
      .decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (cancelled || !result) return;
        const code = result.getText();
        const now = Date.now();
        const last = lastCameraScanRef.current;
        if (last && last.code === code && now - last.at < CAMERA_SAME_CODE_COOLDOWN_MS) {
          return;
        }
        lastCameraScanRef.current = { code, at: now };
        handleScan(code);
      })
      .then((c) => {
        if (cancelled) {
          c.stop();
        } else {
          controls = c;
        }
      })
      .catch(() => {
        setError('No se pudo acceder a la cámara. Revisa los permisos del navegador.');
      });

    return () => {
      cancelled = true;
      controls?.stop();
    };
  }, [method, handleScan]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const isDone = total > 0 && countedProducts >= total;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" minHeight="60vh" px={3} py={4}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate('/dashboard/counting')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {countedProducts} de {total} SKUs con al menos 1 unidad contada
          </Typography>
        </Box>

        <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 4, mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isDone && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Todos los SKUs de esta sesión tienen al menos una unidad contada. Puedes seguir
            escaneando para sumar más, o volver al inicio.
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {method === 'camera' ? 'Apunta la cámara al código de barras' : 'Escanea con el lector físico'}
            </Typography>

            {method === 'camera' ? (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4 / 3',
                  bgcolor: 'black',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
              </Box>
            ) : (
              <TextField
                inputRef={scannerInputRef}
                autoFocus
                fullWidth
                label="Esperando escaneo…"
                value={scannerInput}
                onChange={(e) => setScannerInput(e.target.value)}
                onKeyDown={handleScannerKeyDown}
                onBlur={() => setTimeout(() => scannerInputRef.current?.focus(), 100)}
              />
            )}

            {submittingProductId !== null && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={20} />
              </Box>
            )}
          </CardContent>
        </Card>

        {lastScan && (
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Último escaneo
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {lastScan.productName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SKU {lastScan.sku}
                  </Typography>
                  {lastScan.location && (
                    <Typography variant="body2" color="primary.main">
                      {lastScan.location}
                    </Typography>
                  )}
                </Box>
                <Chip color="success" label={`${lastScan.tally} unidades`} />
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ScanCountingPanel;
